/** Account management: the list, the add form, and inline editing. */

import { useState } from 'react';
import type { AccountSummary } from '@sflx/shared';
import { AccountCard } from '../components/AccountCard';
import { AccountForm, type AccountFormValues } from '../components/AccountForm';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { accounts as accountsApi, type CreateAccountBody } from '../lib/api';
import { useAccounts } from '../lib/accounts-store';
import { playlistWatchPath } from '../lib/playlist-path';
import { useSession } from '../lib/session';
import { navigate } from '../router/router';

export function Accounts() {
  const { accounts, loading, error, reload, setActive } = useAccounts();
  const { user } = useSession();
  const admin = user?.role === 'admin';
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AccountSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<unknown>(null);

  const create = async (values: Partial<AccountFormValues>) => {
    setBusy(true);
    setFormError(null);
    try {
      const created = await accountsApi.create(values as CreateAccountBody);
      await reload();
      setActive(created.account.id);
      setAdding(false);
      if (created.account.importScope) {
        navigate(playlistWatchPath(created.account.importScope, created.account.id));
      }
      // Deliberately no redirect to the country picker: the histogram it shows
      // is empty until the live ingest has actually written categories, and the
      // account card polls that job and links to the picker itself.
    } catch (caught) {
      setFormError(caught);
    } finally {
      setBusy(false);
    }
  };

  const update = async (id: string, values: Partial<AccountFormValues>) => {
    setBusy(true);
    setFormError(null);
    try {
      await accountsApi.update(id, values);
      await reload();
      setEditing(null);
      if (values.importScope) navigate(playlistWatchPath(values.importScope, id));
    } catch (caught) {
      setFormError(caught);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !accounts.length) {
    return (
      <div className="page">
        <Loading label="Loading accounts" />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="wrap stack">
        <div className="page__head">
          <div className="page__title">
            <h1>{admin ? 'Playlists' : 'Library'}</h1>
            <span className="muted">
              {admin
                ? 'Protected servers, and public iptv-org playlists.'
                : 'Playlists set up by the administrator. You can watch; you cannot change them.'}
            </span>
          </div>
          {admin && !adding ? (
            <button type="button" className="btn btn--primary" onClick={() => { setAdding(true); setFormError(null); }}>
              <Icon name="plus" size={16} />
              Add playlist
            </button>
          ) : null}
        </div>

        <ErrorNotice error={error} />

        {admin && adding ? (
          <AccountForm
            busy={busy}
            error={formError}
            submitLabel="Add playlist"
            onSubmit={(values) => void create(values)}
            onCancel={() => setAdding(false)}
          />
        ) : null}

        {!accounts.length && !adding ? (
          <EmptyState
            title={admin ? 'No playlists yet' : 'Nothing to watch yet'}
            hint={
              admin
                ? 'Choose US Only or All Countries to play a public playlist, or enter a server URL with a username and password.'
                : 'Ask the administrator to set up a playlist and invite you.'
            }
            action={
              admin ? (
                <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
                  <Icon name="plus" size={16} />
                  Add playlist
                </button>
              ) : undefined
            }
          />
        ) : null}

        {accounts.map((account) =>
          admin && editing?.id === account.id ? (
            <AccountForm
              key={account.id}
              account={account}
              busy={busy}
              error={formError}
              submitLabel="Save changes"
              onSubmit={(values) => void update(account.id, values)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <AccountCard
              key={account.id}
              account={account}
              writable={admin}
              onChanged={() => void reload()}
              onEdit={() => {
                setFormError(null);
                setEditing(account);
              }}
            />
          ),
        )}
      </div>
    </div>
  );
}
